/* eslint-disable max-lines */
import { useSOP, useUpdateLocationAlarm } from '@hakimo-ui/hakimo/data-access';
import {
  QuickResolveActionItem,
  Situation,
  StatusType,
} from '@hakimo-ui/hakimo/types';
import { toast } from '@hakimo-ui/hakimo/util';
import { Dispatch, useMemo } from 'react';
import { useAlarmGroupStatic } from '../unified-alarm-group/alarm-provider/AlarmGroupProvider';
import { getQuickResolveActionLabel } from '../sop-manager/type-guards';
import { updateDefaultValues } from '../sop-workflow/util';
import { buildSOPCards, getSOPFreshness } from './sop-card-utils';
import { SOPV2Action } from './useSOPV2Machine';

interface ResolveOptions {
  isScanContext: boolean;
  locationAlarmId: string;
  onAlarmResolve: (status: StatusType, comment?: string) => void;
  onQuickResolve?: (
    comment: string,
    action?: string,
    subAction?: string
  ) => void;
  track: (
    event:
      | 'alarm_group_sop_resolve_submit'
      | 'alarm_group_sop_quick_resolve_select',
    payload: Record<string, unknown>
  ) => void;
  dispatch: Dispatch<SOPV2Action>;
}

export function useSOPV2Resolve(options: ResolveOptions) {
  const {
    isScanContext,
    locationAlarmId,
    onAlarmResolve,
    onQuickResolve,
    track,
    dispatch,
  } = options;

  const { dataVersion } = useAlarmGroupStatic();

  const resolveMutation = useUpdateLocationAlarm(
    isScanContext ? '' : locationAlarmId,
    {
      onSuccess: () => {
        toast('Alarm status updated', { type: 'success' });
        onAlarmResolve(StatusType.RESOLVED);
      },
      dataVersion: isScanContext ? undefined : dataVersion,
    }
  );

  const onSubmitResolveAction = async (
    comment: string,
    action?: QuickResolveActionItem,
    subAction?: string
  ) => {
    track('alarm_group_sop_resolve_submit', {
      ...(action && { action: getQuickResolveActionLabel(action) }),
      ...(subAction && { subAction }),
    });
    if (isScanContext) {
      onQuickResolve?.(
        comment,
        action ? getQuickResolveActionLabel(action) : '',
        subAction
      );
    } else {
      resolveMutation.mutate({ status: StatusType.RESOLVED, comment });
    }
  };

  const onQuickResolveSubmit = (comment: string) => {
    track('alarm_group_sop_quick_resolve_select', { action: comment });
    if (isScanContext) {
      onQuickResolve?.(comment);
    } else {
      onSubmitResolveAction(comment);
    }
  };

  const handleQuickResolve = (
    actionLabel: string,
    action?: QuickResolveActionItem
  ) => {
    const isOthers = actionLabel.toLowerCase() === 'others';
    const hasSubActions =
      !!action &&
      typeof action === 'object' &&
      (action.subActions?.length ?? 0) > 0;

    if (isOthers || hasSubActions) {
      dispatch({
        type: 'QUICK_RESOLVE_NEEDS_DETAIL',
        action: (!isOthers && action) || undefined,
      });
      return;
    }
    onQuickResolveSubmit(actionLabel);
  };

  return { resolveMutation, onSubmitResolveAction, handleQuickResolve };
}

interface SopOptions {
  locationId: string;
  locationTimezone: string;
  alarmStartEpochMs?: number;
  alarmEndEpochMs?: number;
  onSituationSelect?: (situation: Situation) => void;
}

export function useSOPV2Sop(options: SopOptions) {
  const {
    locationId,
    locationTimezone,
    alarmStartEpochMs,
    alarmEndEpochMs,
    onSituationSelect,
  } = options;

  const { isLoading, isError, data: sopData } = useSOP({ locationId });

  const sopWorkflow = useMemo(() => {
    const workflow = sopData?.sop_workflow;
    if (!workflow) {
      return undefined;
    }
    return updateDefaultValues(workflow);
  }, [sopData]);

  const cards = useMemo(
    () =>
      buildSOPCards(sopWorkflow, {
        onSituationSelect,
        alarmStartEpochMs,
        alarmEndEpochMs,
        locationTimezone,
      }),
    [
      sopWorkflow,
      onSituationSelect,
      alarmStartEpochMs,
      alarmEndEpochMs,
      locationTimezone,
    ]
  );

  const sectionsContent = useMemo(
    () =>
      Object.fromEntries(
        cards.map((card) => [
          card.id,
          { title: card.title, content: card.content },
        ])
      ),
    [cards]
  );

  return {
    sopWorkflow,
    sopData,
    isLoading,
    isError,
    cards,
    sectionsContent,
    freshness: getSOPFreshness(sopData?.updated_at_utc),
    hasNoSOP: !isLoading && !isError && !sopWorkflow,
  };
}

export type SOPData = ReturnType<typeof useSOPV2Sop>;
